import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  FaBox,
  FaImages,
  FaEnvelope,
  FaPlus,
  FaEdit,
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
} from "react-icons/fa";
import { fetchProducts } from "../../store/slices/productSlice";
import { fetchGalleryImages } from "../../store/slices/gallerySlice";
import { fetchInquiries } from "../../store/slices/contactSlice";

const statusConfig = {
  new: { label: "New", cls: "bg-red-100 text-red-700", icon: FaExclamationCircle },
  read: { label: "Read", cls: "bg-yellow-100 text-yellow-700", icon: FaClock },
  responded: { label: "Responded", cls: "bg-green-100 text-green-700", icon: FaCheckCircle },
  closed: { label: "Closed", cls: "bg-gray-100 text-gray-600", icon: FaCheckCircle },
};

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const { products, pagination: productPagination } = useSelector((state) => state.products);
  const { images, pagination: galleryPagination } = useSelector((state) => state.gallery);
  const { inquiries, pagination: inquiryPagination } = useSelector((state) => state.contact);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchProducts({ limit: 5 }));
    dispatch(fetchGalleryImages({ limit: 5 }));
    dispatch(fetchInquiries({ limit: 5 }));
  }, [dispatch]);

  const stats = [
    {
      title: "Total Products",
      value: productPagination?.totalProducts ?? products?.length ?? 0,
      icon: FaBox,
      color: "bg-blue-500",
      light: "bg-blue-50 text-blue-600",
      link: "/admin/products",
      action: "Add Product",
      actionLink: "/admin/products/new",
    },
    {
      title: "Gallery Images",
      value: galleryPagination?.totalImages ?? images?.length ?? 0,
      icon: FaImages,
      color: "bg-emerald-500",
      light: "bg-emerald-50 text-emerald-600",
      link: "/admin/gallery",
      action: "Upload Image",
      actionLink: "/admin/gallery/upload",
    },
    {
      title: "Total Inquiries",
      value: inquiryPagination?.totalInquiries ?? inquiries?.length ?? 0,
      icon: FaEnvelope,
      color: "bg-violet-500",
      light: "bg-violet-50 text-violet-600",
      link: "/admin/inquiries",
      action: "View All",
      actionLink: "/admin/inquiries",
    },
  ];

  const newInquiries = inquiries?.filter((i) => i.status === "new").length || 0;
  const recentProducts = products?.slice(0, 4) || [];
  const recentInquiries = inquiries?.slice(0, 5) || [];

  const userName = user?.firstName
    ? `${user.firstName} ${user.lastName || ""}`.trim()
    : user?.username || "Admin";

  return (
    <>
      <Helmet>
        <title>Dashboard - OM Maruti Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Welcome banner */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
          <p className="text-blue-100 text-sm font-medium mb-1">Welcome back 👋</p>
          <h1 className="text-2xl font-bold">{userName}</h1>
          <p className="text-blue-200 text-sm mt-1">
            Here's what's happening with your store today.
          </p>
          {newInquiries > 0 && (
            <Link
              to="/admin/inquiries"
              className="mt-4 inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-sm px-4 py-2 rounded-lg transition font-medium"
            >
              <FaEnvelope size={13} />
              {newInquiries} new {newInquiries === 1 ? "inquiry" : "inquiries"} waiting
              <FaArrowRight size={11} />
            </Link>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.title} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-11 h-11 rounded-xl ${stat.light} flex items-center justify-center`}>
                    <Icon className="text-lg" />
                  </div>
                  <Link
                    to={stat.actionLink}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    <FaPlus size={10} />
                    {stat.action}
                  </Link>
                </div>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-gray-500 text-sm mt-0.5">{stat.title}</p>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link
              to="/admin/products/new"
              className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition group"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                <FaPlus className="text-white text-sm" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Add Product</p>
                <p className="text-xs text-gray-500">Upload a new product</p>
              </div>
            </Link>
            <Link
              to="/admin/gallery/upload"
              className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition group"
            >
              <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <FaImages className="text-white text-sm" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Upload Image</p>
                <p className="text-xs text-gray-500">Add gallery image</p>
              </div>
            </Link>
            <Link
              to="/admin/inquiries"
              className="flex items-center gap-3 p-4 rounded-xl bg-violet-50 hover:bg-violet-100 transition group"
            >
              <div className="w-9 h-9 rounded-lg bg-violet-600 flex items-center justify-center flex-shrink-0">
                <FaEnvelope className="text-white text-sm" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">View Inquiries</p>
                <p className="text-xs text-gray-500">Respond to customers</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Two-column: recent products + recent inquiries */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Products */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Recent Products</h2>
              <Link to="/admin/products" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                View all <FaArrowRight size={10} />
              </Link>
            </div>
            {recentProducts.length > 0 ? (
              <div className="space-y-3">
                {recentProducts.map((product) => (
                  <div key={product._id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition">
                    <img
                      src={product.image || "/placeholder-product.jpg"}
                      alt={product.name}
                      className="w-11 h-11 object-cover rounded-lg flex-shrink-0 bg-gray-100"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                      <p className="text-xs text-gray-400">{product.category}</p>
                    </div>
                    <Link
                      to={`/admin/products/${product._id}/edit`}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition flex-shrink-0"
                    >
                      <FaEdit size={13} />
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaBox className="mx-auto text-gray-300 text-3xl mb-2" />
                <p className="text-gray-400 text-sm">No products yet</p>
                <Link to="/admin/products/new" className="text-blue-600 text-sm font-medium mt-1 inline-block">
                  Add your first product
                </Link>
              </div>
            )}
          </div>

          {/* Recent Inquiries */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Recent Inquiries</h2>
              <Link to="/admin/inquiries" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                View all <FaArrowRight size={10} />
              </Link>
            </div>
            {recentInquiries.length > 0 ? (
              <div className="space-y-3">
                {recentInquiries.map((inquiry) => {
                  const cfg = statusConfig[inquiry.status] || statusConfig.new;
                  const StatusIcon = cfg.icon;
                  return (
                    <div key={inquiry._id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-500 font-semibold text-sm">
                        {inquiry.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{inquiry.name}</p>
                        <p className="text-xs text-gray-400 truncate">{inquiry.subject}</p>
                      </div>
                      <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
                        <StatusIcon size={10} />
                        {cfg.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaEnvelope className="mx-auto text-gray-300 text-3xl mb-2" />
                <p className="text-gray-400 text-sm">No inquiries yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
